import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { WordCountComponent } from '@pwc/pwc-deals-ui';
import FieldCommentWrapper from 'common/components/FieldCommentWrapper';
import TextareaAutosize from 'react-autosize-textarea/lib';
import { CommentTarget, TextUtilities } from '@pwc/pwc-deals-web-common';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import './TitleFieldWithWordCount.scss';
import { STRUCTURED_ANALYSIS_V1_TITLES } from '../../engine/utilities/constants';

const TitleFieldsetWithWordCount = ({
  field: { value, name },
  form: { setFieldValue },
  placeholder,
  suggestedLength,
  dataTestId = `title-field-with-wordcount-for-${name}`,
  errorMessage,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (e) => {
      setFieldValue(name, e.target.value);
    },
    [name, setFieldValue]
  );

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = useCallback(
    (e) => {
      setIsFocused(false);
      const currentValue = e.target.value;
      const trimmedText = TextUtilities.trimText(currentValue);
      if (currentValue !== trimmedText) {
        setFieldValue(name, trimmedText);
      }
    },
    [name, setFieldValue]
  );

  const titleElement = useMemo(() => {
    const title = (
      <TextareaAutosize
        name={name}
        value={value}
        className={classNames(
          'title-field-counted-field',
          isFocused && 'title-field-counted-field--focused'
        )}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        data-testid={dataTestId}
      />
    );
    if (!STRUCTURED_ANALYSIS_V1_TITLES.includes(value)) {
      return (
        <FieldCommentWrapper fieldKey={CommentTarget.Title}>
          {title}
        </FieldCommentWrapper>
      );
    }

    return title;
  }, [
    dataTestId,
    handleBlur,
    handleChange,
    isFocused,
    name,
    placeholder,
    value,
  ]);

  const readOnlyTitleElement = useMemo(() => {
    const valueElement = (
      <p
        className={classNames(
          'title-field-counted-decorator',
          !value && 'title-field-counted-decorator--placeholder'
        )}
      >
        {value || placeholder}
      </p>
    );
    if (!STRUCTURED_ANALYSIS_V1_TITLES.includes(value)) {
      return (
        <FieldCommentWrapper fieldKey={CommentTarget.Title}>
          {valueElement}
        </FieldCommentWrapper>
      );
    }

    return valueElement;
  }, [placeholder, value]);

  return (
    <div
      className={classNames(
        'title-field-counted-container',
        errorMessage && 'title-container-error'
      )}
    >
      {!isFocused && readOnlyTitleElement}
      <div
        className={classNames(
          'hoverable-field',
          isFocused && 'hoverable-field--focused'
        )}
      >
        {titleElement}
      </div>
      {errorMessage && <ErrorMessage msg={errorMessage} />}
      <WordCountComponent
        charCount={(value || '').length}
        allowedCount={suggestedLength}
      />
    </div>
  );
};

TitleFieldsetWithWordCount.propTypes = {
  field: PropTypes.shape({
    value: PropTypes.string,
    name: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func,
  }).isRequired,
  placeholder: PropTypes.string,
  suggestedLength: PropTypes.number,
};

TitleFieldsetWithWordCount.defaultProps = {
  placeholder: 'Title',
  suggestedLength: 80,
};

export default TitleFieldsetWithWordCount;
